import { useEffect, useState } from 'react';
import { API } from '../../api';
import { useProgramContext } from '../../hooks/useProgramContext';
import { MedalKey, MedalSection } from './scorecardData';

// Live data for the admin gamification bar's Tier panel — wraps
// GET /gamification/admin/summary: the five learner medal sections pooled
// across the org's learners (Σ completed / Σ completable, test accounts
// excluded; program admins get their administered programs only).
//
// Real data or nothing: this returns the adapted medals or `null`, and
// consumers hide the Tier panel until real numbers arrive. The hardcoded org
// medals this used to fall back to shipped to prod and were reported as wrong
// org data (RDP, Sep 2026).

const getAuthHeaders = () => {
  try {
    const raw = localStorage.getItem('tokens');
    const tokens = raw ? JSON.parse(raw) : {};
    return { idtoken: tokens.id_token || '', authorization: tokens.access_token || '' };
  } catch {
    return { idtoken: '', authorization: '' };
  }
};

export interface AdminGamificationMedals {
  overall: MedalKey;
  sections: MedalSection[];
}

// Section metadata the BE doesn't repeat (measure line + band boundaries).
const SECTION_MEASURES: Record<string, string> = {
  lessonCompletion: 'Lessons completed across all learners, as a % of all assigned lessons',
  knowledgeRetention: 'Average score across all lesson quiz assessments',
  knowledgeMastery: '% of attempted live scenarios passed, retakes included',
  learningRange: '% of program enrolments started across all learners',
  proactiveLearning: '% of learners who messaged Elliott in Conversations in the last 30 days'
};
const PCT_BOUNDARIES: [number, number, number] = [25, 50, 75];

// All five sections are percentages on the admin panel, Proactive Learning
// included — it's the share of learners who engaged at all, so a handful of
// heavy users can no longer carry a pool that has stopped asking questions.
const adaptSection = (s: any): MedalSection => ({
  name: s?.name || '',
  measure: SECTION_MEASURES[s?.key] || '',
  pos: Math.min(typeof s?.pct === 'number' ? s.pct : 0, 100),
  value: s?.value || '',
  medal: (s?.medal || 'wood') as MedalKey,
  boundaries: PCT_BOUNDARIES
});

const adaptMedals = (data: any): AdminGamificationMedals | null => {
  if (!data || !Array.isArray(data.sections) || data.sections.length === 0) return null;
  return {
    overall: (data.overall || 'wood') as MedalKey,
    sections: data.sections.map(adaptSection)
  };
};

export const useAdminGamification = (
  // Narrow the pool to these Teams-list values; omit for the admin's whole remit.
  teamIds?: string[],
  // Skip the fetch entirely (e.g. the Tier view isn't showing).
  options?: { skip?: boolean }
): {
  medals: AdminGamificationMedals | null;
  loading: boolean;
} => {
  const { location } = useProgramContext();
  const [medals, setMedals] = useState<AdminGamificationMedals | null>(null);
  const [loading, setLoading] = useState(true);
  const skip = options?.skip === true;
  // Stable dependency for the array.
  const teamKey = (teamIds || []).slice().sort().join(',');

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchSummary = async () => {
      const { idtoken, authorization } = getAuthHeaders();
      if (!idtoken || !authorization) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await API.getAdminGamificationSummary({
          idtoken,
          authorization,
          location,
          teamIds: teamKey ? teamKey.split(',') : undefined
        });
        if (!cancelled) setMedals(adaptMedals(res?.data));
      } catch {
        // Silent — the Tier chip stays hidden rather than showing fake numbers.
        if (!cancelled) setMedals(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [location, teamKey, skip]);

  return { medals, loading };
};
